import styled, {
  FlattenSimpleInterpolation,
  css,
} from "styled-components/macro";
import {
  skuTitle,
  machineBreakdownGrey,
  skuBreakdownGrey,
} from "../../../../constants/colors";
import {
  BREAKPOINT_MOBLET,
  BREAKPOINT_TABLET,
  BREAKPOINT_TABTOP,
  FONT_SIZE_EXTRA_SMALL,
  FONT_SIZE_MEDIUM,
  FONT_SIZE_SMALL,
} from "../../../../constants/constants";
import {
  MACHINE_BREAKDOWN_WIDTH_OFFSET_PERCENT,
  MACHINE_BREAKDOWN_WIDTH_PERCENT,
  BREAKDOWN_ROW_PADDING_EXPANDED_PX,
} from "./metricAndPnLBreakdownUtils";
import { BREAKPOINT_DESKTOP } from "constants/constants";

export const METRICS_WIDTH_PERCENT = 12;

export const SingleMetricAndPnLBreakdownStyles = styled.div`
  width: ${MACHINE_BREAKDOWN_WIDTH_PERCENT}%;
  border-radius: 10px 10px 0px 0px;
  margin: 1em auto;

  @media print {
    margin: 1em auto 0;
  }

  @media (max-width: ${BREAKPOINT_TABTOP}px),
    (max-width: ${BREAKPOINT_MOBLET}px),
    print {
    width: 90%;
  }
`;

export const MetricAndPnLBreakdownStyles = styled.div<{
  $isExpanded: boolean;
  $shouldShowButterfly?: boolean;
  $shouldExpand?: boolean;
}>`
  border-radius: 10px;
  background-color: ${props =>
    props.$shouldShowButterfly ? machineBreakdownGrey : "white"};
  cursor: ${props => (props.$shouldExpand ? "pointer" : "default")};
  ${props =>
    props.$shouldShowButterfly
      ? ""
      : css`
          transform: translateX(3em);
        `}
  @media (max-width: ${BREAKPOINT_TABTOP}px),
    (max-width: ${BREAKPOINT_MOBLET}px) {
  }
  .bottomButtonsRow {
    display: flex;
    padding: ${BREAKDOWN_ROW_PADDING_EXPANDED_PX / 2}px;
    justify-content: end;
    gap: 1em;
    @media print {
      display: none;
    }
  }
  ${props =>
    props.$isExpanded
      ? css`
          margin-bottom: 12px;
        `
      : ""}
`;

type BreakdownRowStylesType = {
  $numMetrics: number;
  $percentWidthOfMetricColumns: number[];
  $maxBreakdownHeaderLength: number;
  $isSkuBreakdown: boolean;
  $isExpanded: boolean;
  $shouldShowButterfly: boolean;
  $isFirstRow?: boolean;
  $isLastRow?: boolean;
  $customCss: string | FlattenSimpleInterpolation;
};

export const BreakdownRowStyles = styled.div<BreakdownRowStylesType>`
  box-sizing: border-box;
  margin: auto;
  display: grid;
  min-height: 64px;
  grid-template-columns:
    minmax(
      ${props => props.$maxBreakdownHeaderLength * 12}px,
      ${props => props.$maxBreakdownHeaderLength * 14}px
    )
    auto;
  grid-column-gap: 2em;
  background-color: ${props =>
    props.$isSkuBreakdown
      ? skuBreakdownGrey
      : props.$isExpanded
        ? machineBreakdownGrey
        : "white"};
  ${props =>
    props.$isFirstRow
      ? css`
          border-top-right-radius: 10px;
          border-top-left-radius: 10px;
        `
      : css`
          margin-top: -${BREAKDOWN_ROW_PADDING_EXPANDED_PX * 0.5}px;
        `}
  ${props =>
    props.$isLastRow
      ? css`
          border-bottom-right-radius: 10px;
          border-bottom-left-radius: 10px;
        `
      : css``}
  align-items: center;

  padding-top: ${props =>
    props.$isSkuBreakdown
      ? BREAKDOWN_ROW_PADDING_EXPANDED_PX * 0.75
      : props.$isExpanded
        ? BREAKDOWN_ROW_PADDING_EXPANDED_PX
        : BREAKDOWN_ROW_PADDING_EXPANDED_PX * 0.75}px;
  padding-left: ${BREAKDOWN_ROW_PADDING_EXPANDED_PX}px;
  padding-right: ${BREAKDOWN_ROW_PADDING_EXPANDED_PX * 3}px;
  padding-bottom: ${props =>
    props.$isExpanded
      ? BREAKDOWN_ROW_PADDING_EXPANDED_PX
      : BREAKDOWN_ROW_PADDING_EXPANDED_PX * 0.75}px;

  &:hover {
    z-index: 999;
    position: relative;
  }

  .machineHeader {
    display: flex;
    flex-direction: column;
    gap: 0.5em;
    width: 100%;
    height: 100%;
    justify-content: center;
    .machineName {
      display: flex;
      align-items: center;
      font-size: ${props =>
        props.$isSkuBreakdown
          ? FONT_SIZE_EXTRA_SMALL
          : FONT_SIZE_MEDIUM - props.$maxBreakdownHeaderLength / 4}px;
      font-weight: ${props => (props.$isSkuBreakdown ? 400 : 700)};
    }

    .skusTitle {
      font-size: ${FONT_SIZE_EXTRA_SMALL}px;
      color: ${skuTitle};
      font-weight: 400;
      &.add-space-right {
        margin-right: -72px;
      }
    }

    position: relative;
    .btnMenu {
      position: absolute;
      height: fit-content;
      margin: auto;
      left: -16px;
      top: 0px;
      bottom: 0px;
      @media (max-width: ${BREAKPOINT_TABLET}px) {
        bottom: unset;
        left: unset;
        right: 0;
      }
    }
  }

  .summaryMetrics {
    display: flex;
    gap: 0.5em;
    flex-direction: column;
    font-size: ${FONT_SIZE_SMALL - 3}px;
    width: ${props => (props.$shouldShowButterfly ? "100%" : "fit-content")};
  }

  .summaryMetricsBar {
    display: grid;
    ${props => css`
      grid-template-columns: ${props.$numMetrics * METRICS_WIDTH_PERCENT}% ${100 -
        props.$numMetrics * METRICS_WIDTH_PERCENT}%;
    `}
    width: 100%;
    justify-content: center;
    align-items: center;

    .fullBar {
      display: flex;
      align-items: center;
      border-radius: 3px;

      .breakdown {
        display: flex;
        width: ${props =>
          props.$shouldShowButterfly ? "100%" : "fit-content"};
      }
    }
  }

  .metrics {
    display: grid;
    align-items: center;
    grid-template-columns: ${props =>
      props.$percentWidthOfMetricColumns?.map(elem => `${elem}% `)};
  }

  .metric {
    display: flex;
    justify-content: flex-start;
  }

  .metricsTitle {
    display: grid;
    grid-template-columns: ${props =>
      props.$percentWidthOfMetricColumns?.map(elem => `${elem}% `)};

    align-items: center;
    width: ${props => props.$numMetrics * METRICS_WIDTH_PERCENT}%;
  }
  .metrics,
  .metricsTitle {
    grid-gap: 1em;
  }
  ${props =>
    props.$shouldShowButterfly
      ? css`
          width: 100%;
          .fullBar {
            transform: translateX(50px);
          }
        `
      : css`
          width: fit-content;
        `}
  @media print {
    margin: 1em auto 0;

    grid-template-columns: ${props =>
        50 + props.$maxBreakdownHeaderLength * 3}px auto;
    padding: 0 ${MACHINE_BREAKDOWN_WIDTH_OFFSET_PERCENT / 4}% 10px;

    .machineName {
      font-size: ${props =>
        FONT_SIZE_MEDIUM - props.$maxBreakdownHeaderLength / 1.5}px;
    }

    .summaryMetrics {
      font-size: ${FONT_SIZE_EXTRA_SMALL}px;
    }
  }

  @media screen and (max-width: ${BREAKPOINT_DESKTOP}px) {
    box-sizing: border-box;
    ${props => (props.$isSkuBreakdown ? css`` : "")}
    padding-top: 0px;
    padding-bottom: 0px;
    min-height: 64px;
    margin: 0;
    margin-left: -32px;
    width: calc(100% + 32px);
    .machineHeader {
      padding-left: 32px;
    }
  }
  @media screen and (max-width: ${BREAKPOINT_TABLET}px) {
    display: flex;
    flex-direction: column;
    margin: 0em auto;
    margin-left: -16px;
    padding: 1em 10px 10px;

    .machineName {
      font-size: ${FONT_SIZE_SMALL}px;
      font-weight: 700;
    }

    .summaryMetrics {
      font-size: ${FONT_SIZE_SMALL - 4}px;
      width: 100%;
    }

    .metrics,
    .skuMetrics {
      width: 100%;
    }

    .skuMetrics {
      padding-top: 5px;
      font-size: ${FONT_SIZE_EXTRA_SMALL - 2}px;
    }

    .metricsTitle {
      grid-template-columns: repeat(${props => props.$numMetrics}, 1fr);
    }

    .summaryMetricsBar {
      display: flex;
      flex-direction: column;
      width: 100%;

      .fullBar {
        width: 100%;
        transform: none;
      }

      .metrics {
        padding-bottom: 1em;
      }
    }

    .machineHeader {
      align-self: flex-start;
      padding-bottom: 10px;
    }

    .metricsTitle {
      grid-template-columns: repeat(${props => props.$numMetrics}, 1fr);
      align-items: center;
    }
  }
  @media print {
    .fullBar {
      width: 80%;
    }
  }
  ${props => props.$customCss ?? ``}
`;

export const SkuBreakdownStyles = styled.div`
  background-color: ${skuBreakdownGrey} !important;
  padding-bottom: ${BREAKDOWN_ROW_PADDING_EXPANDED_PX * 0.5}px;
`;
